import { Fragment, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import Step from 'components/global/Step'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useVouchers } from 'store/vouchers-slice/hooks'
import { setDeliveryType } from 'store/order-slice'
import {
    calculateVouchersAddToCartEvent,
    pushDataLayer,
} from 'helpers/analytics'
import {
    SingleCustomAmountVoucher,
    SingleCustomCardVoucher,
} from 'components/shared/SingleVoucher'
import Loader from 'components/shared/Loader'
import Section from 'components/shared/Section'
import { useAutoScroll } from 'utils/hooks'
import { formatPrice } from 'helpers/helpers'
import { checkCartVouchersCoherence } from 'helpers/vouchers'

const VouchersSummary = () => {
    const { formatMessage } = useIntl()
    const VOUCHER_NAME = formatMessage({
        id: 'vouchers.summary.voucher',
        defaultMessage: 'Voucher',
    })
    const isRendered = useRef<boolean>(false)
    const analyticsRef = useRef<boolean>(false)
    const dispatch = useAppDispatch()
    const { removeShipping, clearItemsFromCart } = useVouchers()
    const { calculateScroll } = useAutoScroll()

    const { vouchers } = useAppSelector((state) => state.cart.items)
    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)

    const handleBackToVouchersEditing = (index: number) => {
        calculateScroll({
            // TODO: reconsider 'trigerringSection' name - misleading when using remote scroll
            trigerringSection: 'vouchers_configuration',
            trigerringAction: 'edit',
            isRemote: true,
            remoteId: `voucher-${index}`,
            remoteDelay: 1000,
        })
    }

    useEffect(() => {
        if (!vouchers.length) return

        // clear cart if vouchers types are somehow mixed
        if (!checkCartVouchersCoherence(vouchers)) {
            clearItemsFromCart()
            dispatch(setDeliveryType('email'))
            return
        }

        // < analytics
        if (analyticsRef.current) return
        const vouchersAddToCartEvent = calculateVouchersAddToCartEvent(vouchers)

        pushDataLayer(vouchersAddToCartEvent)
        analyticsRef.current = true
        // analytics >
    }, [vouchers])

    useEffect(() => {
        isRendered.current = true
        dispatch(setDeliveryType('email'))
        removeShipping()

        return () => {
            isRendered.current = false
        }
    }, [])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_summary',
                defaultMessage: 'Podsumowanie',
            })}
            isButtonDisabled={false}
            isButtonVisible={true}
        >
            {isRendered.current ? (
                vouchers.map((voucher, index) => {
                    const voucherProps = {
                        voucherName: !vouchers.length
                            ? VOUCHER_NAME
                            : `${VOUCHER_NAME} ${index + 1}`,
                        description: voucher.detail.voucherSignature,
                        index,
                        testIdPrefix: `voucher-${index}`,
                        onClick: vouchersBoxes.length
                            ? () => handleBackToVouchersEditing(index)
                            : undefined,
                    }
                    return (
                        <Fragment key={voucher.cartItemId}>
                            {voucher.detail.voucherType === 'amount' && (
                                <SingleCustomAmountVoucher
                                    valuePrice={
                                        voucher.unitPrice
                                            ? formatPrice(
                                                  voucher.unitPrice,
                                                  true
                                              )
                                            : undefined
                                    }
                                    {...voucherProps}
                                />
                            )}
                            {voucher.detail.voucherType === 'product' && (
                                <SingleCustomCardVoucher
                                    cardPerson={voucher.description}
                                    cardType={
                                        voucher.name
                                            ? voucher.name.toUpperCase()
                                            : undefined
                                    }
                                    {...voucherProps}
                                />
                            )}
                        </Fragment>
                    )
                })
            ) : (
                <Section>
                    <Loader />
                </Section>
            )}
        </Step>
    )
}

export default VouchersSummary
